import api from "../api/api";
import { appState, dispatch, routerHistory } from "../redux/redux-config";
import { isInstalled } from "../utilities/utilities";
import { showToast } from "../components/toast";

const passwordRecovery = {
    forgot: async (username, options = {}) => {
        let {
            showSuccessToast = false,
            successToastMessage = "Reset link sent. Please check your inbox.",
            useGlobalLoading = true,
        } = options;

        if (useGlobalLoading) dispatch("LOGIN_STARTED", true);

        let result = await api.passwordRecovery.forgot(username);

        if (result.success) {
            if (useGlobalLoading) dispatch("LOGIN_STARTED", false);
            dispatch("SWITCH_LOGIN_SCREEN", "verificationCode");
            dispatch("LOGIN_SETTINGS", { resetUsername: username });
            if (showSuccessToast) showToast("success", successToastMessage);
        } else {
            if (useGlobalLoading) dispatch("LOGIN_ERROR", result);
        }

        return result;
    },

    verify: async (code) => {
        let resetUsername = appState().user.resetUsername;
        dispatch("LOGIN_STARTED", true);

        let result = await api.passwordRecovery.verify(resetUsername, code);

        if (result.success) {
            dispatch("LOGIN_STARTED", false);
            dispatch("LOGIN_SETTINGS", {
                resetUsername: null,
                resetId: result.resetToken,
            });
            dispatch("SWITCH_LOGIN_SCREEN", "resetPassword");
        } else if (result.errorMessage?.toLowerCase().includes("expired")) {
            dispatch("LOGIN_STARTED", false);
            dispatch("SWITCH_LOGIN_SCREEN", "linkExpired");
        } else {
            dispatch("LOGIN_ERROR", result);
        }
    },

    resetPassword: async (password) => {
        let resetId = appState().user.resetId;
        dispatch("LOGIN_STARTED", true);

        let result = await api.passwordRecovery.resetPassword(
            resetId,
            password,
            isInstalled() || undefined
        );
        if (result.success) {
            dispatch("LOGIN_STARTED", false);
            // No auto-login: user confirms via the New Password Created screen, then signs in manually.
            dispatch("SWITCH_LOGIN_SCREEN", "passwordCreated");
        } else if (result.errorMessage?.toLowerCase().includes("expired")
            || result.errorMessage?.toLowerCase().includes("invalid token")) {
            dispatch("LOGIN_STARTED", false);
            if (!appState().user.resetUsername) {
                const usernameResult = await api.passwordRecovery.getUsernameForResetId(resetId);
                if (usernameResult?.success && usernameResult.username) {
                    dispatch("LOGIN_SETTINGS", { resetUsername: usernameResult.username });
                }
            }
            dispatch("SWITCH_LOGIN_SCREEN", "linkExpired");
        } else {
            dispatch("LOGIN_ERROR", result);
        }
        return result;
    },

    changeScreen: function (screen) {
        // Clear the /reset?ID=... URL when leaving the reset-via-email-link flow so a
        // subsequent login doesn't land on a URL with no matching authenticated route
        // (which otherwise renders a blank page).
        if (window.location.pathname === "/reset") {
            routerHistory.replace("/");
        }
        dispatch("SWITCH_LOGIN_SCREEN", screen);
    },

    validateResetToken: async (resetId) => {
        let result = await api.passwordRecovery.validateResetToken(resetId);

        // Only redirect when the server explicitly confirms the token is invalid/expired.
        // If the call itself failed (network error, etc.) fail open and let the user
        // attempt the submit, which performs the same validation server-side anyway.
        if (result?.success && !result.isValid) {
            if (result?.username) {
                dispatch("LOGIN_SETTINGS", { resetUsername: result.username });
            }
            dispatch("SWITCH_LOGIN_SCREEN", "linkExpired");
        }

        return result;
    },

    getResendStatus: async (username) => {
        if (!username) return { success: false };
        return await api.passwordRecovery.getResendStatus(username);
    },
};

export default passwordRecovery;